/**
 * DiffusionCard — Carte « Diffusion » affichée dans la colonne droite de la fiche bien (onglet Aperçu).
 * Remplace l'ancienne carte « Actions rapides » (LOT 3 — iter92).
 *
 * Affiche pour chaque support (SeLoger, Leboncoin, CessionPME, Site vitrine) :
 *   pastille de statut · label · date de mise à jour · lien « Voir l'annonce » si diffusé.
 * Un bouton « Gérer la diffusion » ouvre le DiffusionManagerModal.
 */

import React, { useEffect, useState } from 'react';
import { useRelectureDiffusion } from '../../hooks/useRelectureDiffusion';
import DiffusionManagerModal from './DiffusionManagerModal';
import { DIFFUSION_SUPPORTS, DIFFUSION_STATUS_CONFIG, normalizeDiffusion, filterEnabledSupports } from '../../constants/diffusionConstants';
import { formatDateFR } from '../../utils/formatFR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DiffusionCard = ({ property, onDiffusionChange }) => {
  const [managerOpen, setManagerOpen] = useState(false);
  const [enabledIds, setEnabledIds] = useState(null); // null = loading
  const diffusion = normalizeDiffusion(property?.diffusion);

  // iter103 : source de vérité UNIQUE — data.supports (calculé par le backend
  // via _get_agency_enabled_supports, identique à /diffusion/overview.supports).
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/marketing/agency-social-config`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setEnabledIds(Array.isArray(data.supports) ? data.supports : []);
        } else {
          setEnabledIds([]);
        }
      } catch {
        setEnabledIds([]);
      }
    })();
  }, []);

  const visibleSupports = enabledIds === null ? DIFFUSION_SUPPORTS : filterEnabledSupports(enabledIds);

  // lot BM — dépôt SeLoger en cours : on relit NOTRE api (30 s, 10 min au plus) le temps que
  // la notification du portail arrive. Aucun appel au portail depuis l'écran.
  const relireLeBien = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/properties/${property?.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      if (typeof onDiffusionChange === 'function') onDiffusionChange(data.diffusion);
    } catch { /* silencieux : relecture d'agrément */ }
  };
  useRelectureDiffusion(diffusion?.seloger?.status === 'en_attente', relireLeBien);

  return (
    <>
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
        data-testid="diffusion-card"
      >
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <h3 className="text-[13px] font-semibold text-slate-800 flex items-center gap-2">
            <i className="bi bi-broadcast text-blue-600" />
            Diffusion
          </h3>
          <span className="text-[11px] text-slate-500">{visibleSupports.length} support{visibleSupports.length > 1 ? 's' : ''}</span>
        </div>

        {visibleSupports.length === 0 ? (
          <div className="px-4 py-6 text-center text-[12px] text-slate-500">
            <i className="bi bi-info-circle text-slate-400 text-lg" />
            <p className="mt-1">Aucun support activé.</p>
            <p className="text-[11px] mt-0.5">Configurer dans le Super Admin.</p>
          </div>
        ) : (
        <ul className="divide-y divide-slate-100">
          {visibleSupports.map(support => {
            const state = diffusion[support.id];
            const cfg = DIFFUSION_STATUS_CONFIG[state.status] || DIFFUSION_STATUS_CONFIG.non_diffuse;
            return (
              <li
                key={support.id}
                className="px-4 py-2.5 flex items-center gap-3"
                data-testid={`diffusion-row-${support.id}`}
              >
                <span className={`inline-flex w-8 h-8 rounded-lg items-center justify-center ${support.accent} ring-1 ${support.ring} bg-white shrink-0`}>
                  <i className={`bi ${support.icon} text-sm`} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-medium text-slate-800 truncate">{support.label}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${cfg.text}`} data-testid={`diffusion-status-${support.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} aria-hidden="true" />
                      {cfg.label}
                    </span>
                    {state.date && (
                      <span className="text-[10.5px] text-slate-400 tabular-nums">
                        · {formatDateFR(state.date)}
                      </span>
                    )}
                  </div>
                </div>
                {/* lot BM — SeLoger fournit son URL par notification, même en attente. */}
                {state.url && (state.status === 'diffuse' || support.id === 'seloger') && (
                  <a
                    href={state.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                    data-testid={`diffusion-link-${support.id}`}
                  >
                    {support.id === 'seloger' ? 'Voir sur SeLoger' : 'Voir l\u2019annonce'}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
        )}

        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40">
          <button
            type="button"
            data-testid="diffusion-manage-btn"
            onClick={() => setManagerOpen(true)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold shadow-sm transition"
          >
            <i className="bi bi-sliders" />
            Gérer la diffusion
          </button>
        </div>
      </div>

      {managerOpen && (
        <DiffusionManagerModal
          property={property}
          diffusion={diffusion}
          onClose={() => setManagerOpen(false)}
          onChange={onDiffusionChange}
        />
      )}
    </>
  );
};

export default DiffusionCard;
